import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AuthContext } from './AuthContext';
import { ProductsContext } from './ProductsContext';
import { StoreContext } from './StoreContext';
import { calculateCartEstimate } from '../utils/catalogMoney';
import { createBrowserCartRepository } from '../services/cartStore';
import { createCartSyncNotifier } from '../services/cartSync';

export const CartContext = createContext({
  items: [],
  addToCart: async () => false,
  removeFromCart: async () => false,
  updateQuantity: async () => false,
  clearCart: async () => false,
  markCheckoutSubmitted: async () => ({ state: 'submitted', created: false }),
  removePurchasedItems: async () => ({ applied: false }),
  cartReady: false,
  cartPersistenceError: null,
  cartSubtotal: null,
  cartTax: null,
  estimatedTotal: null,
  cartPricingValid: false,
  products: [],
  imageItems: [],
  getCoverImage: () => null,
});

/**
 * The optional repository/notifier arguments make transaction and component
 * behavior testable without weakening the browser's IndexedDB requirement.
 */
export function CartProvider({ children, repository: providedRepository, notifier: providedNotifier }) {
  const repository = useMemo(
    () => providedRepository || createBrowserCartRepository(),
    [providedRepository],
  );
  const notifier = useMemo(
    () => providedNotifier || createCartSyncNotifier(),
    [providedNotifier],
  );
  const [cart, setCart] = useState({ items: [], revision: -1 });
  const [cartReady, setCartReady] = useState(false);
  const [cartPersistenceError, setCartPersistenceError] = useState(null);
  const { user } = useContext(AuthContext);
  const { products, imageItems, getCoverImage } = useContext(ProductsContext);
  const { store } = useContext(StoreContext);

  const applyCartSnapshot = useCallback(snapshot => {
    if (!snapshot || !Array.isArray(snapshot.items)) return;
    setCart(current => (
      Number.isSafeInteger(snapshot.revision) && snapshot.revision >= current.revision
        ? snapshot
        : current
    ));
  }, []);

  useEffect(() => {
    let disposed = false;
    let refreshSequence = 0;

    const recordFailure = error => {
      if (disposed) return;
      setCartPersistenceError(error instanceof Error
        ? error
        : new Error('The cart could not be loaded.'));
    };

    repository.initialize()
      .then(snapshot => {
        if (disposed) return;
        applyCartSnapshot(snapshot);
        setCartPersistenceError(null);
        setCartReady(true);
      })
      .catch(recordFailure);

    const unsubscribe = notifier.subscribe(async () => {
      const sequence = ++refreshSequence;
      try {
        const snapshot = await repository.readCart();
        if (disposed || sequence !== refreshSequence) return;
        applyCartSnapshot(snapshot);
        setCartPersistenceError(null);
        setCartReady(true);
      } catch (error) {
        recordFailure(error);
      }
    });

    return () => {
      disposed = true;
      unsubscribe();
      notifier.close?.();
      Promise.resolve(repository.close?.()).catch(() => {});
    };
  }, [applyCartSnapshot, notifier, repository]);

  const runCartMutation = useCallback(async operation => {
    try {
      const result = await operation();
      applyCartSnapshot(result.cart);
      setCartPersistenceError(null);
      setCartReady(true);
      if (result.changed) notifier.publish();
      return true;
    } catch (error) {
      setCartPersistenceError(error instanceof Error
        ? error
        : new Error('The cart change could not be saved.'));
      return false;
    }
  }, [applyCartSnapshot, notifier]);

  const addToCart = useCallback(
    (product, selectedModifiers = [], quantityToAdd = 1) => runCartMutation(
      () => repository.addItem(product, selectedModifiers, quantityToAdd),
    ),
    [repository, runCartMutation],
  );

  const removeFromCart = useCallback(
    itemId => runCartMutation(() => repository.removeItem(itemId)),
    [repository, runCartMutation],
  );

  const updateQuantity = useCallback(
    (itemId, newQuantity) => runCartMutation(
      () => repository.updateQuantity(itemId, newQuantity),
    ),
    [repository, runCartMutation],
  );

  const clearCart = useCallback(
    () => runCartMutation(() => repository.clear()),
    [repository, runCartMutation],
  );

  const markCheckoutSubmitted = useCallback(async ({
    userId,
    quoteId,
    purchasedItems,
  }) => {
    if (!user?.uid || userId !== user.uid) {
      throw new Error('The authenticated user does not match this checkout submission.');
    }
    try {
      const result = await repository.markCheckoutSubmitted({
        userId,
        quoteId,
        purchasedItems,
      });
      setCartPersistenceError(null);
      return result;
    } catch (error) {
      setCartPersistenceError(error instanceof Error
        ? error
        : new Error('The checkout submission could not be saved safely.'));
      throw error;
    }
  }, [repository, user?.uid]);

  const removePurchasedItems = useCallback(async (purchasedItems, reference) => {
    if (!user?.uid) {
      throw new Error('Authentication is required to reconcile a confirmed order.');
    }
    try {
      const result = await repository.reconcilePurchasedItems({
        userId: user.uid,
        referenceType: reference?.referenceType,
        referenceId: reference?.referenceId,
        purchasedItems,
      });
      applyCartSnapshot(result.cart);
      setCartPersistenceError(null);
      setCartReady(true);
      if (result.applied) notifier.publish();
      return result;
    } catch (error) {
      setCartPersistenceError(error instanceof Error
        ? error
        : new Error('The confirmed order could not be reconciled with the cart.'));
      throw error;
    }
  }, [applyCartSnapshot, notifier, repository, user?.uid]);

  const items = cart.items;
  const estimate = calculateCartEstimate(items, products, store);
  const cartSubtotal = estimate.subtotalMinor;
  const cartTax = estimate.taxMinor;
  const estimatedTotal = estimate.totalMinor;
  const cartPricingValid = estimate.valid;
  const cartLoading = !cartReady || (products.length === 0 && items.length > 0);

  const value = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    markCheckoutSubmitted,
    removePurchasedItems,
    cartReady,
    cartPersistenceError,
    cartSubtotal,
    cartTax,
    estimatedTotal,
    cartPricingValid,
    cartLoading,
    products,
    imageItems,
    getCoverImage,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
